import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {createDocumentAPI, getClientListAPI} from '../../../services/admin';
import styles from './CreateAdminDocument.module.css';
import {ClientListResponseData, CreateDocumentRequest} from "../../../types/admin";
import {DocumentType} from "../../../types/document";
import AdminLayout from "../layout/AdminLayout";
import FileInput from '../../../components/common/input/FileInput';
import MultiSelect from '../../../components/common/select/MultiSelect';
import {useAlert} from "../../common/error/AlertContext";

//어드민 문서 생성 페이지
const CreateAdminDocument: React.FC = () => {
    const navigate = useNavigate();
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();
    const [formData, setFormData] = useState({
        title: '',
        type: DocumentType.견적서,
        content: '',
        clientIds: [] as number[],
        emailTitle: '',
        emailContent: ''
    });
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [availableScopes, setAvailableScopes] = useState<ClientListResponseData[]>([]);
    const [sendEmail, setSendEmail] = useState(false);

    useEffect(() => {
        const fetchAvailableScopes = async () => {
            try {
                //클라이언트 목록 조회 API, 공개 대상 클라이언트 선택용
                const response = await getClientListAPI();
                setAvailableScopes(response);
            } catch (error) {
                console.error('Failed to fetch available scopes:', error);
            }
        };
        fetchAvailableScopes();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    //공개 대상 클라이언트 선택
    const handleScopeChange = (selectedIds: number[]) => {
        setFormData(prev => ({
            ...prev,
            clientIds: selectedIds
        }));
    };
    //첨부 파일 등록
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            setFile(e.target.files[0]);
        } else {
            setFile(null);
        }
    };
    //문서 생성 알림 메일 템플릿
    const handleEmailCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSendEmail(e.target.checked);
        if (!e.target.checked) {
            setFormData(prev => ({
                ...prev,
                emailTitle: '',
                emailContent: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            //필수 입력 검증
            if (!formData.title.trim()) {
                showAlert('제목을 입력해주세요.', "error");
                return;
            }
            if (!formData.content.trim()) {
                showAlert('내용을 입력해주세요.', "error");
                return;
            }
            if (sendEmail && (!formData.emailTitle.trim() || !formData.emailContent.trim())) {
                showAlert('이메일 제목과 내용을 입력해주세요.', "error");
                return;
            }
            setLoading(true);

            const createRequest: CreateDocumentRequest = {
                title: formData.title.trim(),
                type: formData.type,
                content: formData.content.trim(),
                clientIds: formData.clientIds,
                emailTitle: sendEmail ? formData.emailTitle.trim() : '',
                emailContent: sendEmail ? formData.emailContent.trim() : ''
            };
            //문서 생성 API
            await createDocumentAPI(createRequest, file);
            showAlert('문서가 성공적으로 생성되었습니다.', "info");
            //문서 테이블 페이지로 이동
            navigate('/admin/documents');
        } catch (error) {
            console.error('문서 생성 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    //취소
    const handleCancel = () => {
        //작성 중인 내용이 있는 경우
        if (formData.title.trim() || formData.content.trim() || file) {
            //확인창
            showConfirm(
                '작성 중인 내용이 있습니다. 취소하시겠습니까?',
                () => {
                    //문서 테이블 페이지로 이동
                    navigate('/admin/documents')
                }
            )
        } else {
            navigate('/admin/documents');
        }
    };

    return (
        <AdminLayout>
            <div className={styles.container}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>문서 생성</h1>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="title" className={styles.label}>제목</label>
                        {/*제목 입력*/}
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="제목을 입력하세요"
                            maxLength={100}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="type" className={styles.label}>문서 종류</label>
                        {/*문서 종류 선택*/}
                        <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className={styles.select}
                            required
                        >
                            {Object.values(DocumentType)
                                .map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="content" className={styles.label}>내용</label>
                        {/*문서 내용 작성*/}
                        <textarea
                            id="content"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            className={styles.textarea}
                            placeholder="내용을 입력하세요"
                            rows={10}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="clientIds" className={styles.label}>공개 대상 클라이언트</label>
                        {/*공개 대상 클라이언트 선택*/}
                        <MultiSelect
                            options={availableScopes.map(scope => ({
                                value: scope.clientId,
                                label: scope.companyName
                            }))}
                            value={formData.clientIds}
                            onChange={handleScopeChange}
                            placeholder="공개 대상 클라이언트를 선택하세요"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        {/*첨부 파일 등록*/}
                        <FileInput
                            label="첨부파일"
                            onChange={handleFileChange}
                            value={file}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            이메일 전송
                            {/*공개 대상 클라이언트에게 메일 전송 여부 체크박스*/}
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                onChange={handleEmailCheckChange}
                                className={styles.checkbox}
                            />
                        </label>
                    </div>

                    {/*이메일 전송 시*/}
                    {sendEmail && (
                        <>
                            <div className={styles.formGroup}>
                                <label htmlFor="emailTitle" className={styles.label}>이메일 제목</label>
                                {/*이메일 제목 입력*/}
                                <input
                                    type="text"
                                    id="emailTitle"
                                    name="emailTitle"
                                    value={formData.emailTitle}
                                    onChange={handleInputChange}
                                    className={styles.input}
                                    placeholder="이메일 제목을 입력하세요"
                                    required={sendEmail}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="emailContent" className={styles.label}>이메일 내용</label>
                                {/*이메일 내용 입력*/}
                                <textarea
                                    id="emailContent"
                                    name="emailContent"
                                    value={formData.emailContent}
                                    onChange={handleInputChange}
                                    className={styles.textarea}
                                    placeholder="이메일 내용을 입력하세요"
                                    rows={5}
                                    required={sendEmail}
                                />
                            </div>
                        </>
                    )}

                    <div className={styles.buttonGroup}>
                        {/*취소 버튼*/}
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={styles.cancelButton}
                        >
                            취소
                        </button>
                        {/*저장 버튼*/}
                        <button
                            type="submit"
                            disabled={loading || !formData.title.trim() || !formData.content.trim()}
                            className={styles.submitButton}
                        >
                            {loading ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default CreateAdminDocument;